import * as Sentry from '@sentry/react'
import { useCardData } from 'components/providers/CardDataProvider'
import { getOtherUserStock, getUserStock } from 'lib/api'
import { useGradingScale } from 'lib/hooks/useGradingScale'
import { useCallback, useMemo, useRef } from 'react'
import {
  Article,
  Card,
  CardmarketCondition,
  CardmarketStockAttributes,
  CARDMARKET_CONDITIONS,
  FINISH_TYPE_LABELS,
  getPtAttributesFromCardmarketArticleAttributes,
  IdGame,
  InventoryArticle,
  isSingle,
  parsePokemonFinishType,
  parsePtStructuredComment,
} from 'shared'

export interface FetchedCardmarketStock {
  stock: InventoryArticle[]
  sealedStock: InventoryArticle[]
}

export const useFetchCardmarketStock = () => {
  const cardData = useCardData()
  // Whether a grade written in a comment is one its company really awards is a question only the table answers,
  // and the hook already holds the answer, cached for the whole page.
  const { gradingScale } = useGradingScale()

  // The fetch is one long loop over every game, started once. A callback that captured the scale before it had
  // arrived would keep parsing every article against the empty one — letting a retired grade through unchecked and
  // sending a perfectly good labelled grade to the manual step. Read through a ref, each article is parsed against
  // whatever the scale is at that moment.
  const gradingScaleRef = useRef(gradingScale)
  gradingScaleRef.current = gradingScale

  const cardDataMap = useMemo(() => {
    const map = new Map<number, Card>()
    for (const card of cardData) {
      map.set(card._id, card)
    }
    return map
  }, [cardData])

  const processArticles = useCallback(
    (rawArticles: CardmarketStockAttributes[], idGame: IdGame): InventoryArticle[] => {
      const result: InventoryArticle[] = []
      for (const cmArticle of rawArticles) {
        const article = new Article({
          attributes: getPtAttributesFromCardmarketArticleAttributes(
            cmArticle,
            idGame,
            gradingScaleRef.current
          ),
        })
        const card = cardDataMap.get(article.idProduct)

        const condition = cmArticle[1]
        const price = cmArticle[4]
        const isConditionInvalid = !CARDMARKET_CONDITIONS.includes(condition as CardmarketCondition)

        if (card?.name.toLocaleLowerCase().includes('rainbow foil')) {
          article.attributes[15] = FINISH_TYPE_LABELS.RAINBOW_FOIL
          article.finishType = FINISH_TYPE_LABELS.RAINBOW_FOIL
        }
        if (card?.name.toLocaleLowerCase().includes('reverse holo')) {
          article.attributes[15] = FINISH_TYPE_LABELS.REVERSE_HOLO
          article.finishType = FINISH_TYPE_LABELS.REVERSE_HOLO
        }
        if (card?.name.toLocaleLowerCase().includes('cold foil')) {
          article.attributes[15] = FINISH_TYPE_LABELS.COLD_FOIL
          article.finishType = FINISH_TYPE_LABELS.COLD_FOIL
        }

        if (idGame === IdGame.Pokemon) {
          const { finishType: structuredFinishType } = parsePtStructuredComment(
            cmArticle[5],
            idGame,
            gradingScaleRef.current
          )
          if (!structuredFinishType) {
            const updatedFinishType = parsePokemonFinishType(article.comment, !!cmArticle[8])
            article.attributes[15] = updatedFinishType
            article.finishType = updatedFinishType
          }
        }

        if (card) {
          if (isSingle(card) && isConditionInvalid) {
            Sentry.withScope((scope) => {
              scope.setTag('component', 'useFetchCardmarketStock')
              scope.setExtra('context', 'Invalid or missing condition in parsed article')
              scope.setExtra('cmArticle (raw input)', cmArticle)
              scope.setExtra('parsedCondition', condition)
              scope.setExtra('parsedPrice', price)
              scope.setExtra('idProduct', article.idProduct)
              scope.setExtra('parsed ptArticle', article)
              Sentry.captureMessage('Invalid Cardmarket condition value during stock import')
            })
          }

          if (!isSingle(card) && isConditionInvalid) {
            article.attributes[1] = 'NM' // set default NM only for sealed
          }

          result.push(
            new InventoryArticle({
              attributes: article.attributes,
              card: card.toPlainObject(),
              ptArticleReservedQuantity: 0,
            })
          )
        }
      }
      return result
    },
    [cardDataMap]
  )

  const fetchCardmarketStock = useCallback(
    async (idGame: IdGame, tokens = null): Promise<FetchedCardmarketStock> => {
      if (tokens) {
        // Legacy other-user path returns a flat array of sealed-only articles.
        const flat = (await getOtherUserStock(
          idGame,
          true,
          tokens
        )) as unknown as CardmarketStockAttributes[]
        return { stock: [], sealedStock: processArticles(flat, idGame) }
      }

      const data = (await getUserStock(idGame)) as unknown as {
        stock: CardmarketStockAttributes[]
        sealedStock: CardmarketStockAttributes[]
      }

      return {
        stock: processArticles(data?.stock ?? [], idGame),
        sealedStock: processArticles(data?.sealedStock ?? [], idGame),
      }
    },
    [processArticles]
  )

  return fetchCardmarketStock
}
